import { usePostHog } from 'posthog-js/react';
import { useTranslation } from 'react-i18next';
import {
  BookingType,
  CodeExceptionStatus,
  CodesService,
  OrderService,
  PassService,
  PaymentsService,
  VoucherService,
  VoucherType,
} from '../../api';
import { applyCodeErrorStatus } from '../../shared/errors/applyCodeErrors';
import {
  isDatedSessionUnavailableError,
  isSlotCapacityError,
  passPurchaseErrorMessage,
} from '../../shared/errors/passPurchaseErrors';
import { StepId } from '../intentConfig/types';
import { toPaymentUrl } from '../steps/payments/squarePaymentForm';
import { useLanguage } from '../../utils/GeneralUtils';
import { useOrderPage } from '../orderPageContext/useOrderPage';
import { useIntentConfig } from '../intentConfig/useIntentConfig';
import { OrderPageIntent } from '../OrderPageIntent';
import { getDepositInfo } from '../core/pricing';
import { getUtmDataFromLocalStorage } from '../orderCompletion/utmLocalStorage';
import { getSessionIdSync } from '../../session';
import { clearOrderPageStateFromLocalStorage } from '../orderPageContext/orderPageStateLocalStorage';

type UseSaveOrderParams = {
  embeddedWidget: boolean;
  // The optional message replaces the generic save-failure copy when the cause is specific enough
  // for the customer to act on.
  onSaveError: (message?: string) => void;
};

export function useSaveOrder({ embeddedWidget, onSaveError }: UseSaveOrderParams) {
  const { reloadDates, orderPageState, setActiveStep, setOrderPageState } = useOrderPage();
  const { config, getStepIndex } = useIntentConfig();
  const language = useLanguage();
  const posthog = usePostHog();
  const { t } = useTranslation();
  const { voucher, orderPageIntent } = orderPageState;

  const getPaymentForm = async (order: any) => {
    const paymentForm = await PaymentsService.getPaymentForm({
      language,
      account_id: orderPageState.account.id,
      experience_id: orderPageState.experience?.id,
      order_page_id: orderPageState.orderPage.id,
      entity_id: order.id,
      payment_form_type: config!.paymentFormType!,
    });
    return toPaymentUrl(paymentForm);
  };

  const saveOrder = async (personalDetails?: any) => {
    const currentQueryParams = new URLSearchParams(window.location.search);
    const currentSessionId = currentQueryParams.get('session_id') || getSessionIdSync() || undefined;

    if (orderPageIntent === OrderPageIntent.BUY_PASS) {
      try {
        const passPd = personalDetails || orderPageState.personalDetails;
        const {
          pass_start_date: passStartDate,
          save_card_consent: passSaveCardConsent,
          ...passPersonalDetails
        } = passPd;
        const pass = await PassService.createPass({
          language,
          ...passPersonalDetails,
          ...(passStartDate ? { start_date: passStartDate } : {}),
          ...(orderPageState.passScheduleSelections?.length
            ? { schedule_selections: orderPageState.passScheduleSelections }
            : {}),
          ...(orderPageState.passDatedSessionSelections?.length
            ? { dated_session_selections: orderPageState.passDatedSessionSelections }
            : {}),
          account_id: orderPageState.account.id,
          order_page_id: orderPageState.orderPage.id,
          price: orderPageState.orderPage.price,
          can_spam: orderPageState.canSpam,
          utm_data: getUtmDataFromLocalStorage(),
          session_id: currentSessionId,
          from_embedded_widget: embeddedWidget,
          save_card_consent_accepted: !!passSaveCardConsent,
        });
        if (!currentSessionId && embeddedWidget) {
          posthog.capture('Order Saved Without Session', {
            orderPageId: orderPageState.orderPage.id,
            accountId: orderPageState.account.id,
            orderPageIntent,
            passId: pass?.id,
          });
        }
        return pass;
      } catch (e: any) {
        console.error(e);
        // A weekly slot can fill up between loading the availability grid and submitting, and the
        // schedule validator has its own reasons -- say which one it was rather than "couldn't save".
        // On capacity, send them back to the picker: it reloads availability and drops whatever is
        // now full, so they cannot resubmit the same taken slot.
        if (isSlotCapacityError(e)) {
          const scheduleStep = getStepIndex(StepId.PASS_SCHEDULE_SELECTIONS);
          if (scheduleStep >= 0) {
            setActiveStep(scheduleStep);
          }
        }
        if (isDatedSessionUnavailableError(e)) {
          const unavailable = {
            order_page_id: e.body.order_page_id,
            date: e.body.date,
            start_time: e.body.start_time,
          };
          const datedStep = getStepIndex(StepId.PASS_DATED_SESSION_SELECTIONS);
          setOrderPageState({
            ...orderPageState,
            passDatedSessionSelections: orderPageState.passDatedSessionSelections.filter(
              (selection) =>
                !(
                  selection.order_page_id === unavailable.order_page_id &&
                  selection.date === unavailable.date &&
                  selection.start_time === unavailable.start_time
                ),
            ),
            passDatedSessionUnavailable: unavailable,
            ...(datedStep >= 0 ? { activeStep: datedStep } : {}),
          });
        }
        onSaveError(passPurchaseErrorMessage(e, t) ?? undefined);
        return undefined;
      }
    }
    if (orderPageIntent === OrderPageIntent.GIFT_CARD || orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD) {
      try {
        const voucherPd = personalDetails || orderPageState.personalDetails;
        const { pass_start_date: _voucherPassStartDate, ...voucherPersonalDetails } = voucherPd;
        const { resource_option_id: _voucherResourceOptionId, ...voucherDetails } = orderPageState.details;
        const voucherRequestDetails =
          orderPageIntent === OrderPageIntent.MONETARY_GIFT_CARD
            ? { type: VoucherType.MONETARY, price: voucherDetails.price, persons: 1, duration: 0, custom_details: {} }
            : voucherDetails;
        const voucher = await VoucherService.createVoucher({
          language,
          ...voucherPersonalDetails,
          account_id: orderPageState.account.id,
          experience_id: orderPageState.experience.id,
          order_page_id: orderPageState.orderPage.id,
          ...voucherRequestDetails,
          can_spam: orderPageState.canSpam,
          utm_data: getUtmDataFromLocalStorage(),
          session_id: currentSessionId,
          from_embedded_widget: embeddedWidget,
        });
        if (!currentSessionId && embeddedWidget) {
          posthog.capture('Order Saved Without Session', {
            orderPageId: orderPageState.orderPage.id,
            accountId: orderPageState.account.id,
            orderPageIntent,
            voucherId: voucher?.id,
          });
        }
        return voucher;
      } catch (e) {
        console.error(e);
        onSaveError();
        return undefined;
      }
    }
    try {
      const orderDetails = { ...orderPageState.details };

      const depositInfo = getDepositInfo({
        widgetOrderPageData: orderPageState.orderPage,
        details: orderPageState.details,
        dateDetails: orderPageState.dateAndTimeDetails,
        account: orderPageState.account,
      });

      const orderPd = personalDetails || orderPageState.personalDetails;
      const {
        pass_start_date: _orderPassStartDate,
        save_card_consent: orderSaveCardConsent,
        ...orderPersonalDetails
      } = orderPd;
      const order = await OrderService.saveOrder({
        language,
        account_id: orderPageState.account.id,
        experience_id: orderPageState.experience.id,
        order_page_id: orderPageState.orderPage.id,
        details: orderDetails,
        date_and_time_details: {
          date: orderPageState.dateAndTimeDetails.date ?? undefined,
          time: orderPageState.dateAndTimeDetails.time ?? undefined,
        },
        personal_details: orderPersonalDetails,
        can_spam: orderPageState.canSpam,
        utm_data: getUtmDataFromLocalStorage(),
        session_id: currentSessionId,
        from_embedded_widget: embeddedWidget,
        course_id: orderPageState.dateAndTimeDetails.courseId,
        save_card_consent_accepted: !!orderSaveCardConsent,
        ...(orderPageState.isWalkIn
          ? { booking_type: BookingType.WALK_IN, deposit_price: 0 }
          : depositInfo
          ? { deposit_price: depositInfo.amount }
          : {}),
        ...(orderPageState.projectSelection?.length ? { project_selection: orderPageState.projectSelection } : {}),
      });
      if (orderPageIntent === OrderPageIntent.REDEEM_GIFT_CARD) {
        try {
          await CodesService.applyCode({
            entity_id: order.id,
            code: voucher!.code,
          });
        } catch (e: any) {
          if (applyCodeErrorStatus(e) === CodeExceptionStatus.VOUCHER_LIMIT_REACHED) {
            reloadDates();
            setActiveStep(0);
            throw new Error('voucher_limit_reached');
          }
          throw e;
        }
      }
      if (!currentSessionId && embeddedWidget) {
        posthog.capture('Order Saved Without Session', {
          orderPageId: orderPageState.orderPage.id,
          accountId: orderPageState.account.id,
          orderPageIntent,
          orderId: order?.id,
        });
      }
      return order;
    } catch (e) {
      console.error(e);
      clearOrderPageStateFromLocalStorage();
      setActiveStep(0);
      reloadDates();
      onSaveError();
      return undefined;
    }
  };

  return { saveOrder, getPaymentForm };
}
